import { useContext } from 'react';
import { Box, Grid, Typography, alpha } from '@mui/material';
import { AppContext } from '../../../../context/AppContextProvider';
import { AppThemeName, THEME_PREVIEW_COLORS } from '../../../../styles/global.styles';

const THEME_LABELS: Record<AppThemeName, string> = {
  BLUE: 'Azul',
  BLACK_WHITE: 'Preto e branco',
};

const THEME_DESCRIPTIONS: Record<AppThemeName, string> = {
  BLUE: 'Tema padrão do sistema, com azul como cor de destaque.',
  BLACK_WHITE: 'Preto como cor principal e branco nos detalhes e destaques.',
};

interface ThemePreviewCardProps {
  themeName: AppThemeName;
  selected: boolean;
  onSelect: () => void;
}

// Mini mockup de sidebar + header + card, pintado com as cores literais de cada tema (não usa
// var(--app-*) aqui de propósito: as CSS vars só refletem o tema atualmente aplicado, então pra
// comparar os dois lado a lado o preview precisa das cores fixas de cada opção).
const ThemePreviewCard = ({ themeName, selected, onSelect }: ThemePreviewCardProps) => {
  const c = THEME_PREVIEW_COLORS[themeName];

  return (
    <Box
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? c['--app-accent'] : 'divider',
        boxShadow: selected ? `0 0 0 3px ${alpha(c['--app-accent'], 0.25)}` : 'none',
        overflow: 'hidden',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: c['--app-accent'],
        },
      }}
    >
      {/* Mini preview da UI */}
      <Box sx={{ display: 'flex', height: 110, backgroundColor: c['--app-bg'] }}>
        <Box
          sx={{
            width: 34,
            backgroundColor: c['--app-surface-2'],
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 1.5,
            gap: 1,
          }}
        >
          <Box sx={{ width: 4, height: 18, borderRadius: 1, backgroundColor: c['--app-accent'] }} />
          <Box sx={{ width: 14, height: 3, borderRadius: 1, backgroundColor: alpha(c['--app-text'], 0.5) }} />
          <Box sx={{ width: 14, height: 3, borderRadius: 1, backgroundColor: alpha(c['--app-text'], 0.3) }} />
          <Box sx={{ width: 14, height: 3, borderRadius: 1, backgroundColor: alpha(c['--app-text'], 0.3) }} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              height: 22,
              backgroundColor: c['--app-surface-header'],
              borderBottom: `1px solid ${c['--app-table-border']}`,
            }}
          />
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
            <Box
              sx={{
                borderRadius: 1,
                backgroundColor: c['--app-surface-1'],
                border: `1px solid ${c['--app-table-border']}`,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                px: 1,
              }}
            >
              <Box sx={{ width: '55%', height: 4, borderRadius: 1, backgroundColor: alpha(c['--app-text'], 0.6) }} />
            </Box>
            <Box
              sx={{
                borderRadius: 1,
                backgroundColor: c['--app-accent-bg'],
                border: `1px solid ${c['--app-accent']}`,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                px: 1,
              }}
            >
              <Box sx={{ width: '40%', height: 4, borderRadius: 1, backgroundColor: c['--app-accent'] }} />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          backgroundColor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {THEME_LABELS[themeName]}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ maxWidth: 220 }}>
            {THEME_DESCRIPTIONS[themeName]}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: selected ? c['--app-accent'] : 'divider',
            backgroundColor: selected ? c['--app-accent'] : 'transparent',
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
};

const ThemeManagementTab = () => {
  const { theme, setTheme } = useContext(AppContext);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Tema de Navegação
      </Typography>
      <Typography variant="body1" sx={{ mt: 1, mb: 3, maxWidth: 620 }}>
        Escolha o esquema de cores da navegação (barra lateral, cabeçalho, tabelas e telas) para
        todas as pessoas que acessarem esta licença. A mudança é aplicada imediatamente.
      </Typography>

      <Grid container spacing={2} sx={{ maxWidth: 620 }}>
        {(Object.keys(THEME_PREVIEW_COLORS) as AppThemeName[]).map((themeName) => (
          <Grid item xs={12} sm={6} key={themeName}>
            <ThemePreviewCard
              themeName={themeName}
              selected={theme === themeName}
              onSelect={() => setTheme(themeName)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ThemeManagementTab;
